'use client';

// apps/web/src/components/public/AdTracker.tsx
// Client tracking wrapper for a rendered advertisement (Régie-b). Fires an
// anonymous, fire-and-forget impression beacon once on mount, and a click beacon
// on activation (WITHOUT preventDefault, so navigation proceeds). No visible
// state, no layout effect — it only wraps the creative in the click target.
//
// Beacons hit the BFF under the PUBLIC prefix (/api/public/...), which the
// catch-all forwards to /api/v1/public/medias/:id/(vue|lien) — an
// unauthenticated route. navigator.sendBeacon survives unload (the browser
// queues it past navigation); fetch(keepalive) is the fallback for the rare
// engine without sendBeacon.

import * as React from 'react';

function track(path: string): void {
  const url = `/api/public/medias/${path}`;
  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(url)
    ) {
      return;
    }
  } catch {
    // sendBeacon can throw (e.g. blocked); fall through to fetch.
  }
  try {
    void fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  } catch {
    // best-effort: a failed beacon must never affect the page.
  }
}

export function AdTracker({
  idPublicite,
  href,
  newTab,
  className,
  children,
}: {
  idPublicite: number;
  href: string | null;
  newTab: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  // Fire the impression exactly once, even under React StrictMode's double-invoke
  // of mount effects (same fiber -> the ref persists across the replay).
  const impressionSent = React.useRef(false);
  React.useEffect(() => {
    if (impressionSent.current) return;
    impressionSent.current = true;
    track(`${idPublicite}/vue`);
  }, [idPublicite]);

  const onClick = React.useCallback(() => {
    // No preventDefault: the click navigates; the beacon is queued first.
    track(`${idPublicite}/lien`);
  }, [idPublicite]);

  if (href === null) {
    // No destination — still display + count the impression, but not clickable.
    return <div className={className}>{children}</div>;
  }
  return (
    <a
      href={href}
      target={newTab ? '_blank' : undefined}
      rel="noopener noreferrer"
      onClick={onClick}
      className={className}
    >
      {children}
    </a>
  );
}
